import React from 'react';
import {
  categoryToVistaMapping,
  vistaToRubricMapping,
} from 'constants/evaluation';
import FastTooltip from 'components/FastTooltip';
import { calculateJudgeRankings } from 'utils/submissionSorting';

const QualificationSubmissionDetails = ({
  submission,
  getMainDescription,
  rubrics,
  allSubmissions,
}) => {
  // Extract all jury evaluations for this submission (all are completed since we filter at source)
  const allJuryEvaluations = submission.all_judgments || [];
  const hasJuryScoring = allJuryEvaluations.length > 0;

  // Get unique criteria names from all evaluations
  const allCriteria = new Set();
  allJuryEvaluations.forEach(evaluation => {
    if (evaluation.aspect_scores) {
      evaluation.aspect_scores.forEach(aspect => {
        allCriteria.add(aspect.aspect_id);
      });
    }
  });

  const criteriaList = Array.from(allCriteria);

  // Find the appropriate rubric for this submission's category
  const findSubmissionRubric = () => {
    if (!rubrics || !submission.category) return null;

    const vistaCategoryName = categoryToVistaMapping(submission.category);
    const expectedRubricName = vistaToRubricMapping(vistaCategoryName);

    return rubrics.find(
      rubric => rubric.name && rubric.name === expectedRubricName
    );
  };

  const submissionRubric = findSubmissionRubric();

  // Create aspect weights lookup from rubric
  const aspectWeights = {};
  if (submissionRubric && submissionRubric.aspect_list) {
    submissionRubric.aspect_list.forEach(aspectItem => {
      aspectWeights[aspectItem.aspect_id] = aspectItem.weight; // Weight as percentage
    });
  }

  // Rubric and weights loaded

  // Enhanced weighted score calculation using the new data structure
  const juryScoresWithWeights = allJuryEvaluations.map((evaluation, index) => {
    // Use the pre-calculated weighted score from the enhanced data structure
    const calculatedScore = evaluation.weighted_total_score || 0;

    return {
      ...evaluation,
      calculated_weighted_score: calculatedScore,
    };
  });

  // Calculate average submission score from all jury weighted scores
  const validJuryScores = juryScoresWithWeights.filter(
    jury => jury.calculated_weighted_score > 0
  );
  const averageSubmissionScore =
    validJuryScores.length > 0
      ? validJuryScores.reduce(
          (sum, jury) => sum + jury.calculated_weighted_score,
          0
        ) / validJuryScores.length
      : 0;

  // Final scoring calculation completed

  const judgeRankings = calculateJudgeRankings(
    submission,
    allJuryEvaluations,
    allSubmissions ?? []
  );

  return (
    <tr className="bg-gray-50">
      <td colSpan={7} className="py-6 px-6">
        <div className="max-w-5xl mx-auto space-y-4">
          {/* Jury Scoring Table */}
          {hasJuryScoring ? (
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-100 shadow-sm">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                  <svg
                    className="w-5 h-5 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                    />
                  </svg>
                </div>
                <h4 className="font-baijamjuree font-bold text-gray-900 text-lg">
                  Jury Scoring Breakdown
                </h4>
              </div>

              <div className="overflow-y-visible">
                <table className="w-full border border-gray-200 rounded-xl table-fixed bg-white shadow-sm overflow-hidden">
                  <thead className="bg-gradient-to-r from-gray-800 to-gray-700">
                    <tr>
                      <th className="w-4"></th>
                      <th className="border-b border-gray-600 px-2 py-4 text-left text-sm font-semibold text-white w-32">
                        Judges
                      </th>
                      {criteriaList.map((criteriaId: string) => {
                        // Find criteria name from any evaluation that has this criteria
                        let criteriaName = criteriaId;
                        for (const evaluation of allJuryEvaluations) {
                          const aspect = evaluation.aspect_scores?.find(
                            a => a.aspect_id === criteriaId
                          );
                          if (aspect && aspect.aspect_name) {
                            criteriaName = aspect.aspect_name;
                            break;
                          }
                        }

                        // Remove category prefix if present (format: "Category - Aspect")
                        const cleanedCriteriaName = criteriaName.includes(' - ')
                          ? criteriaName.split(' - ').slice(1).join(' - ') // Remove first part before " - "
                          : criteriaName;

                        // Get percentage for this aspect from rubric or evaluation data
                        let aspectWeight = aspectWeights[criteriaId];

                        // If no rubric weight, try to get from evaluation data
                        if (!aspectWeight && allJuryEvaluations.length > 0) {
                          const firstEvaluation = allJuryEvaluations[0];
                          const aspectInEvaluation =
                            firstEvaluation.aspect_scores?.find(
                              a => a.aspect_id === criteriaId
                            );
                          aspectWeight = aspectInEvaluation?.weight;
                        }

                        // If still no weight, use equal distribution
                        if (!aspectWeight && criteriaList.length > 0) {
                          aspectWeight = Math.round(100 / criteriaList.length);
                        }

                        const percentageText = aspectWeight
                          ? ` (${aspectWeight}%)`
                          : '';

                        // Create display name with percentage for column header
                        const displayName =
                          cleanedCriteriaName + percentageText;
                        const fullTooltip = `${cleanedCriteriaName}${percentageText}`;

                        return (
                          <th
                            key={criteriaId}
                            className="border-b border-gray-600 px-1 py-3 text-center text-sm font-semibold text-white w-20"
                          >
                            <FastTooltip
                              tooltip={fullTooltip}
                              className="cursor-help"
                            >
                              <div
                                className="leading-tight break-words text-white"
                                style={{
                                  display: '-webkit-box',
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: 'vertical',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                }}
                              >
                                {displayName}
                              </div>
                            </FastTooltip>
                          </th>
                        );
                      })}
                      <th className="border-b border-gray-600 px-1 py-3 text-center text-sm font-semibold text-white w-16">
                        <div className="text-sm text-white">Rank</div>
                      </th>
                      <th className="border-b border-gray-600 px-1 py-3 text-center text-sm font-semibold text-white w-16">
                        <div className="text-sm text-white">Score</div>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {juryScoresWithWeights.map((evaluation, index) => (
                      <tr
                        key={index}
                        className="hover:bg-blue-50 transition-colors duration-200 bg-white"
                      >
                        <td className="px-2 py-4 text-sm">{index + 1}.</td>
                        <td className="px-2 py-4 text-sm text-gray-900 font-medium border-r border-gray-100">
                          <div className="truncate font-baijamjuree">
                            Judge {index + 1}
                          </div>
                        </td>
                        {criteriaList.map((criteriaId: string) => {
                          const aspect = evaluation.aspect_scores?.find(
                            a => a.aspect_id === criteriaId
                          );
                          return (
                            <td
                              key={criteriaId}
                              className="px-1 py-4 text-center text-sm border-r border-gray-100"
                            >
                              {aspect ? (
                                <span className="inline-flex items-center justify-center w-8 h-8 bg-gray-50 text-gray-700 rounded font-baijamjuree font-semibold text-sm">
                                  {aspect.value || aspect.score || 'N/A'}
                                </span>
                              ) : (
                                <span className="text-gray-400 font-baijamjuree">
                                  -
                                </span>
                              )}
                            </td>
                          );
                        })}
                        <td className="px-1 py-4 text-center text-sm border-r border-gray-100">
                          <div className="leading-tight">
                            <div className="inline-flex items-center justify-center bg-green-50 border border-green-200 text-green-700 rounded w-8 h-8 font-baijamjuree font-extrabold text-sm mb-1">
                              {judgeRankings[evaluation.judge_email]?.rank ||
                                '-'}
                            </div>
                            <div className="text-xs text-gray-500 font-baijamjuree">
                              of{' '}
                              {judgeRankings[evaluation.judge_email]
                                ?.totalEvaluated || 0}
                            </div>
                          </div>
                        </td>
                        <td className="px-1 py-4 text-center text-sm">
                          <span className="inline-flex items-center justify-center bg-orange-50 border border-orange-200 text-orange-700 rounded px-3 py-1.5 font-baijamjuree font-normal text-sm">
                            {evaluation.calculated_weighted_score.toFixed(2)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-lg p-4 border border-gray-100 text-center">
              <p className="font-bai text-sm text-gray-500">
                This submission is awaiting jury evaluation.
              </p>
            </div>
          )}
        </div>
      </td>
    </tr>
  );
};

export default QualificationSubmissionDetails;
